import { CircleOff, type LucideIcon } from 'lucide-react';
import { APPROVAL_STATUS_TONE, type StatusColorTone } from '@/styles/statusColors';
import type {
  InstituteSubmissionSummary,
  InstituteValues,
} from '../types/instituteSubmission.types';

export const SUBMISSION_STATUS = ['PENDING', 'APPROVED', 'REJECTED'] as const;

export const SUBMISSION_STATUS_LABELS: Record<(typeof SUBMISSION_STATUS)[number], string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const SUBMISSION_STATUS_TONE: Record<(typeof SUBMISSION_STATUS)[number], StatusColorTone> = {
  PENDING: APPROVAL_STATUS_TONE.pending,
  APPROVED: APPROVAL_STATUS_TONE.approved,
  REJECTED: APPROVAL_STATUS_TONE.rejected,
};

export const SUBMISSION_TYPE = ['UPDATE', 'INACTIVATE'] as const;

export const SUBMISSION_TYPE_LABELS: Record<(typeof SUBMISSION_TYPE)[number], string> = {
  UPDATE: 'Validation',
  INACTIVATE: 'Inactivate',
};

export const SUBMISSION_TYPE_TONE: Record<(typeof SUBMISSION_TYPE)[number], StatusColorTone> = {
  UPDATE: 'info',
  INACTIVATE: 'warning',
};

export const REMOVAL_REASON = [
  'PERMANENTLY_CLOSED',
  'SHIFTED_TO_NEW_ADDRESS',
  'DUPLICATE_RECORD',
  'TEMPORARILY_INACTIVE',
] as const;

export const REMOVAL_REASON_LABELS: Record<(typeof REMOVAL_REASON)[number], string> = {
  PERMANENTLY_CLOSED: 'Permanently closed',
  SHIFTED_TO_NEW_ADDRESS: 'Shifted to new address',
  DUPLICATE_RECORD: 'Duplicate record',
  TEMPORARILY_INACTIVE: 'Temporarily inactive',
};

export const UPDATE_SUBMISSION_TYPE = 'UPDATE' satisfies (typeof SUBMISSION_TYPE)[number];
export const INACTIVATE_SUBMISSION_TYPE = 'INACTIVATE' satisfies (typeof SUBMISSION_TYPE)[number];

export const REMOVAL_SUBMISSION_TYPES = [
  INACTIVATE_SUBMISSION_TYPE,
] as const satisfies readonly (typeof SUBMISSION_TYPE)[number][];

export const REMOVAL_REQUEST_DISPLAY: Record<
  (typeof REMOVAL_SUBMISSION_TYPES)[number],
  { title: string; icon: LucideIcon }
> = {
  INACTIVATE: { title: 'Inactivate this institute', icon: CircleOff },
};

export const INSTITUTE_VALUE_FIELDS: { key: keyof InstituteValues; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'instituteTypeName', label: 'Institute Type' },
  { key: 'instituteCategory', label: 'Category' },
  { key: 'instituteLevel', label: 'Institute Level' },
  { key: 'eiinNumber', label: 'EIIN' },
  { key: 'examBoardName', label: 'Exam Board' },
  { key: 'address', label: 'Address' },
  { key: 'thanaName', label: 'Thana' },
  { key: 'districtName', label: 'District' },
  { key: 'contactName', label: 'Contact Name' },
  { key: 'contactNumber', label: 'Contact Number' },
  { key: 'academicClasses', label: 'Institute Classes' },
  { key: 'totalStudents', label: 'Total Students' },
  { key: 'purchasePercentage', label: 'Purchase Percentage' },
];

export const INSTITUTE_CATEGORY = ['GOVERNMENT', 'PRIVATE', 'MPO'] as const;

export const INSTITUTE_CATEGORY_LABELS: Record<(typeof INSTITUTE_CATEGORY)[number], string> = {
  GOVERNMENT: 'Government',
  PRIVATE: 'Private',
  MPO: 'MPO',
};

export const INSTITUTE_LEVEL = ['A_TIER', 'B_TIER', 'C_TIER'] as const;

export const INSTITUTE_LEVEL_LABELS: Record<(typeof INSTITUTE_LEVEL)[number], string> = {
  A_TIER: 'A Tier',
  B_TIER: 'B Tier',
  C_TIER: 'C Tier',
};

export const PENDING_SUBMISSION_STATUS = 'PENDING' satisfies (typeof SUBMISSION_STATUS)[number];

export const SUBMISSION_STATUS_PIN_CLASS: Record<(typeof SUBMISSION_STATUS)[number], string> = {
  PENDING: 'text-amber-500',
  APPROVED: 'text-emerald-600',
  REJECTED: 'text-red-500',
};

interface SummaryCardConfig {
  key: keyof InstituteSubmissionSummary;
  label: string;
}

export const SUMMARY_CARDS: SummaryCardConfig[] = [
  { key: 'total', label: 'Total' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

export const DATE_FROM_FILTER_KEY = 'dateFrom';

export const DATE_TO_FILTER_KEY = 'dateTo';

export const LIST_PAGE_SIZE = 20;

export const UNNAMED_INSTITUTE_TITLE = 'Unnamed institute';

export const DETAIL_TABLE_CELL_CLASS = 'border-r px-2 py-1 last:border-r-0';

export const DETAIL_TABLE_HEADER_CELL_CLASS =
  'border-r px-2 py-1 text-left text-xs font-medium text-muted-foreground last:border-r-0';

export const DETAIL_TABLE_FIELD_CELL_CLASS = 'text-left text-xs whitespace-nowrap';

export const GPS_COORDINATE_DECIMALS = 6;
